import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { BQSqlFunctionHelper } from 'src/app/utils/datasource-sql-map/bq-sql-function-helper';
import {
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import {
    multipleSelectComponentCompileSQLTokens,
    segmentContainerIdCompileSQLTokens,
} from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';

export class BiVizDashboardFunnels {
    public static get sqlMap() {
        const sqlMap: IDatasourceSQLMap = {
            dataset: 'bi_viz',
            tableId: 'dashboard_funnels',
            columns: {
                CUSTOM_FUNNELS_FUNNEL_NAME: {
                    columnId: 'CUSTOM_FUNNELS_FUNNEL_NAME',
                    columnSQL: 'funnel_name',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'funnel_name',
                            state
                        );
                    },
                },
                CUSTOM_FUNNELS_STEP_GROUP: {
                    columnId: 'CUSTOM_FUNNELS_STEP_GROUP',
                    columnSQL: 'step_group',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'step_group',
                            state
                        );
                    },
                },
                CUSTOM_FUNNELS_STEP_ID: {
                    columnId: 'CUSTOM_FUNNELS_STEP_ID',
                    columnSQL: 'step_id',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'step_id',
                            state
                        );
                    },
                },
                CUSTOM_FUNNELS_STEP_NAME: {
                    columnId: 'CUSTOM_FUNNELS_STEP_NAME',
                    columnSQL: 'step_name',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'step_name',
                            state
                        );
                    },
                },
                // This field is not applied yet
                CUSTOM_FUNNELS_OUT_REPLAYS: {
                    columnId: 'CUSTOM_FUNNELS_OUT_REPLAYS',
                    columnSQL: 'SAFE_CAST(NULL AS STRING)',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'SAFE_CAST(NULL AS STRING)',
                            state
                        );
                    },
                },
                // This field is not applied yet
                CUSTOM_FUNNELS_FIRST_GAME_ENI2: {
                    columnId: 'CUSTOM_FUNNELS_FIRST_GAME_ENI2',
                    columnSQL: 'SAFE_CAST(NULL AS STRING)',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'SAFE_CAST(NULL AS STRING)',
                            state
                        );
                    },
                },
                ACTIVITY_DATE: {
                    columnId: 'ACTIVITY_DATE',
                    columnSQL: 'timestamp',
                    compileSQLTokens: (state) => {
                        return DatePickerSqlTokensCompiler.compileTokens(
                            'timestamp',
                            state
                        );
                    },
                },
                ACTIVITY_APP_VERSION_GROUP_MINOR: {
                    columnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                    columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                        BQSqlFunctionHelper.VERSION_HIERARCHY,
                        ['player_dim.revenue_sessions.app_version_d30']
                    )}.minor`,
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            `${BQSqlFunctionHelper.getCallSqlStatement(
                                BQSqlFunctionHelper.VERSION_HIERARCHY,
                                ['player_dim.revenue_sessions.app_version_d30']
                            )}.minor`,
                            state
                        );
                    },
                },
                ACTIVITY_APP_VERSION: {
                    columnId: 'ACTIVITY_APP_VERSION',
                    columnSQL: 'player_dim.revenue_sessions.app_version_d30',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.app_version_d30',
                            state
                        );
                    },
                },
                SEGMENT: {
                    columnId: 'SEGMENT',
                    columnSQL: 'player_dim.segments',
                    compileSQLTokens: segmentContainerIdCompileSQLTokens,
                },
                FIREBASE_FIRST_OPEN_DATE: {
                    columnId: 'FIREBASE_FIRST_OPEN_DATE',
                    // in that table first_session_date is partition key so we don't need to reach into player_dim struct
                    columnSQL: 'first_session_date',
                    compileSQLTokens: (state) => {
                        return DatePickerSqlTokensCompiler.compileTokens(
                            'first_session_date',
                            state
                        );
                    },
                },
                FIREBASE_APP_STORE: {
                    columnId: 'FIREBASE_APP_STORE',
                    columnSQL: 'player_dim.revenue_sessions.first_store',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.first_store',
                            state
                        );
                    },
                },
                FIREBASE_COUNTRY_EUROPEAN_UNION: {
                    columnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                    columnSQL: 'player_dim.geo.first_geo.country_european_union',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.geo.first_geo.country_european_union',
                            state
                        );
                    },
                },
                FIREBASE_COUNTRY_TIER: {
                    columnId: 'FIREBASE_COUNTRY_TIER',
                    columnSQL: 'player_dim.geo.first_geo.country_tier_label',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.geo.first_geo.country_tier_label',
                            state
                        );
                    },
                },
                FIREBASE_COUNTRY_IS_RELEVANT_LABEL: {
                    columnId: 'FIREBASE_COUNTRY_IS_RELEVANT_LABEL',
                    columnSQL:
                        'player_dim.geo.first_geo.country_is_relevant_label',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.geo.first_geo.country_is_relevant_label',
                            state
                        );
                    },
                },
                FIREBASE_COUNTRY: {
                    columnId: 'FIREBASE_COUNTRY',
                    columnSQL: 'player_dim.geo.first_geo.unified_country',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.geo.first_geo.unified_country',
                            state
                        );
                    },
                },
                ATTRIBUTION_TRAFFIC_SOURCE: {
                    columnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                    columnSQL: 'player_dim.attribution.unified_traffic',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_traffic',
                            state
                        );
                    },
                },
                ATTRIBUTION_NETWORK_NAME: {
                    columnId: 'ATTRIBUTION_NETWORK_NAME',
                    columnSQL: 'player_dim.attribution.unified_network',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_network',
                            state
                        );
                    },
                },
                FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
                    columnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                    columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                        BQSqlFunctionHelper.VERSION_HIERARCHY,
                        ['player_dim.revenue_sessions.install_version']
                    )}.minor`,
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            `${BQSqlFunctionHelper.getCallSqlStatement(
                                BQSqlFunctionHelper.VERSION_HIERARCHY,
                                ['player_dim.revenue_sessions.install_version']
                            )}.minor`,
                            state
                        );
                    },
                },
                FIREBASE_INSTALL_VERSION: {
                    columnId: 'FIREBASE_INSTALL_VERSION',
                    columnSQL: 'player_dim.revenue_sessions.install_version',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.install_version',
                            state
                        );
                    },
                },
                ETL_PAYER: {
                    columnId: 'ETL_PAYER',
                    columnSQL: 'player_dim.revenue_sessions.payer_label',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.payer_label',
                            state
                        );
                    },
                },
                ETL_REVENUE_BADGE: {
                    columnId: 'ETL_REVENUE_BADGE',
                    columnSQL: 'player_dim.revenue_sessions.revenue_badge',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.revenue_badge',
                            state
                        );
                    },
                },
                ATTRIBUTION_CAMPAIGN_NAME: {
                    columnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                    columnSQL: 'player_dim.attribution.unified_campaign',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_campaign',
                            state
                        );
                    },
                },
            },
        };
        return sqlMap;
    }

    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const dateTrunc = queryContextSettings.dateAggregation
            ? `DATE_TRUNC(date, ${queryContextSettings.dateAggregation})`
            : `date`;

        const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns.map((dsCol) => {
                return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                    dsCol.state,
                    options
                );
            });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroup = splitSelect ? 'split_group' : '';
        const splitGroupBy = splitSelect ? 'split_group, ' : '';
        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        const join: string = sqlTokens.join.join('\n');

        const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';

        return {
            dateTrunc,
            splitSelect,
            /** `'split_group' or '' */
            splitGroup,
            /** `'split_group, ' or '' */
            splitGroupBy,
            where,
            windowTotal,
            join,
        };
    }
}
